import { useMemo } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { MetaTagsLanguageType } from '../../utils/contexts/AppSettings';
import { getAbsoluteUrl } from '../../utils/utils';
import { ISO_ENGLISH, LINK_REL_ALTERNATE, REGIONS } from './PageMetaTagsConstants';
import { PageMetaTagsProps } from './PageMetaTagsTypes';

const generateHrefLangLinks = (languages: MetaTagsLanguageType[], currentPath: string) => {
  const regionalIsoCode: keyof MetaTagsLanguageType = 'Regional Iso Code';
  let isEnLocalePresent = false;

  const createLink = (key: string, href: string, hrefLang: string) => (
    <link key={key} rel={LINK_REL_ALTERNATE} href={href} hrefLang={hrefLang} />
  );

  const linkTags = languages.flatMap((language) => {
    const languageIso = language.Iso.value.toLowerCase();
    let hrefLang = language[regionalIsoCode].value.toLowerCase();

    if (languageIso === ISO_ENGLISH && !hrefLang) {
      hrefLang = languageIso;
      isEnLocalePresent = true;
      return [
        createLink(language.id, getAbsoluteUrl(currentPath), hrefLang),
        createLink(`${language.id}-x-default`, getAbsoluteUrl(currentPath), 'x-default'),
      ];
    }

    if (Object.keys(REGIONS).includes(hrefLang)) {
      return REGIONS[hrefLang].map((lang) =>
        createLink(
          `${language.id}-${hrefLang}-${lang}`,
          getAbsoluteUrl(`/${lang}${currentPath}`),
          lang
        )
      );
    }

    return createLink(language.id, getAbsoluteUrl(`/${hrefLang}${currentPath}`), hrefLang);
  });

  if (!isEnLocalePresent && languages.length > 0) {
    const {
      id,
      [regionalIsoCode]: { value },
    } = languages[0];
    linkTags.unshift(
      createLink(
        `${id}-x-default`,
        getAbsoluteUrl(`/${value.toLowerCase()}${currentPath}`),
        'x-default'
      )
    );
  }

  return linkTags;
};

export const PageMetaTags = ({
  pageDescription,
  currentLanguage,
  noindex,
  nofollow,
  pageThumbnail,
  pageTitle,
  selectedLanguages = [],
  selectedRegions = [],
}: PageMetaTagsProps) => {
  const router = useRouter();
  const currentPath = router.asPath.split('?')[0];

  const hrefLinks = useMemo(
    () => generateHrefLangLinks(selectedLanguages, currentPath),
    [selectedLanguages, currentPath]
  );

  const robotParts = [];

  if (noindex) robotParts.push('noindex');
  if (nofollow) robotParts.push('nofollow');

  const robotText = robotParts.join(',');

  return (
    <Head>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      {pageThumbnail && (
        <>
          <meta property="og:image" content={pageThumbnail} />
          <meta name="thumbnail" content={pageThumbnail} />
        </>
      )}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Jabra" />
      <meta name="msapplication-TileColor" content="#ffffff" />
      <meta name="msapplication-TileImage" content="/favicons/jabra/ms-icon-144x144.png" />
      {robotText && <meta name="robots" content={robotText} />}

      <link
        rel="canonical"
        href={getAbsoluteUrl(
          `${currentLanguage.toLowerCase() === 'en' ? '' : `/${currentLanguage.toLowerCase()}`}${currentPath}`
        )}
      />
      <link rel="shortcut icon" href="/favicons/jabra/favicon-32x32.png" />
      <link rel="apple-touch-icon" sizes="57x57" href="/favicons/jabra/apple-icon-57x57.png" />
      <link rel="apple-touch-icon" sizes="60x60" href="/favicons/jabra/apple-icon-60x60.png" />
      <link rel="apple-touch-icon" sizes="72x72" href="/favicons/jabra/apple-icon-72x72.png" />
      <link rel="apple-touch-icon" sizes="76x76" href="/favicons/jabra/apple-icon-76x76.png" />
      <link rel="apple-touch-icon" sizes="114x114" href="/favicons/jabra/apple-icon-114x114.png" />
      <link rel="apple-touch-icon" sizes="120x120" href="/favicons/jabra/apple-icon-120x120.png" />
      <link rel="apple-touch-icon" sizes="144x144" href="/favicons/jabra/apple-icon-144x144.png" />
      <link rel="apple-touch-icon" sizes="152x152" href="/favicons/jabra/apple-icon-152x152.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/favicons/jabra/apple-icon-180x180.png" />
      <link
        rel="icon"
        type="image/png"
        sizes="192x192"
        href="/favicons/jabra/android-icon-192x192.png"
      />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicons/jabra/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="96x96" href="/favicons/jabra/favicon-96x96.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicons/jabra/favicon-16x16.png" />
      {!!selectedRegions.length && (
        <>
          {selectedRegions.map((region) => (
            <meta
              key={region.id}
              name={`region-${region['Regional Iso Code'].value.toLowerCase()}`}
              content={getAbsoluteUrl(
                `/${region['Regional Iso Code'].value.toLowerCase()}${currentPath}`
              )}
            />
          ))}
        </>
      )}
      {!!hrefLinks.length && <>{hrefLinks.map((lang) => lang)}</>}
    </Head>
  );
};
